import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { 
  Search, 
  ExternalLink, 
  Plus, 
  Zap, 
  Folder, 
  FolderOpen, 
  ChevronRight, 
  ChevronDown,
  MoreVertical,
  Edit,
  Trash2,
  ChevronsDown,
  ChevronsUp,
  ChevronUp,
  ChevronDown as ChevronDownIcon
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import ConditioningSession from './ConditioningSession';
import MobilitySession from './MobilitySession';

const TreeViewEncyclopedia = ({ onAddMove, onAddCombo, onEditCombo, onEditMove }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { user } = useAuth();
  const moves = user?.moves || [];
  const [selectedMove, setSelectedMove] = useState(null);
  const { arsenal, learning, combos, addToArsenal, addToLearning, removeFromArsenal, removeFromLearning, getLearningStats, setMoveSignature, setComboSignature } = useAuth();
  
  // Debug: Log state changes
  useEffect(() => {
    console.log('🌳 TreeViewEncyclopedia - Arsenal updated:', arsenal);
    console.log('🌳 TreeViewEncyclopedia - Learning updated:', learning);
  }, [arsenal, learning]);
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') return document.body.classList.contains('light') ? 'light' : 'dark';
    return 'dark';
  });
  const [showStretch, setShowStretch] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState(new Set(['TopRock', 'Footwork', 'PowerMoves', 'Freezes', 'Transitions', 'Flips', 'Combos']));
  const { folders = {}, updateFolders, moveFolderUp, moveFolderDown, moveSubfolderUp, moveSubfolderDown } = useAuth();

  // États pour les modals
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showSubfolderModal, setShowSubfolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editingSubfolder, setEditingSubfolder] = useState(null);
  const [parentFolder, setParentFolder] = useState(null);
  const [showMoveToFolderModal, setShowMoveToFolderModal] = useState(false);
  const [selectedItemToMove, setSelectedItemToMove] = useState(null);
  const [stretchingMove, setStretchingMove] = useState(null);
  const [showConditioning, setShowConditioning] = useState(false);
  const [showMobility, setShowMobility] = useState(false);

  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(document.body.classList.contains('light') ? 'light' : 'dark'));
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);



  // Organiser les moves et combos dans les dossiers
  const organizeContent = () => {
    if (!folders) return {};
    
    // Tree view now always uses fresh API data
    
    const organized = { ...folders };
    
    // Créer un set des items déjà assignés manuellement
    const manuallyAssignedItems = new Set();
    Object.keys(organized).forEach(folderKey => {
      Object.keys(organized[folderKey].children).forEach(subfolderKey => {
        Object.keys(organized[folderKey].children[subfolderKey].children).forEach(itemId => {
          manuallyAssignedItems.add(itemId);
        });
      });
    });

    // Organiser les moves (priorité: assignation explicite, sinon par type)
    moves.forEach(move => {
      if (manuallyAssignedItems.has(move.id)) return;
      // 1) Si un dossier cible est spécifié sur le move
      if (move.targetFolder && organized[move.targetFolder]) {
        if (move.targetSubfolder) {
          // Si un subfolder est spécifié, l'utiliser
          if (!organized[move.targetFolder].children[move.targetSubfolder]) {
            organized[move.targetFolder].children[move.targetSubfolder] = {
              name: move.targetSubfolder,
              type: 'subfolder',
              children: {}
            };
          }
          organized[move.targetFolder].children[move.targetSubfolder].children[move.id] = {
            ...move,
            contentType: 'move'
          };
        } else {
          // Si pas de subfolder, créer un dossier "General" ou utiliser le premier subfolder existant
          let subfolderKey = 'General';
          if (Object.keys(organized[move.targetFolder].children).length > 0) {
            subfolderKey = Object.keys(organized[move.targetFolder].children)[0];
          } else {
            // Créer un dossier "General" s'il n'y en a pas
            organized[move.targetFolder].children[subfolderKey] = {
              name: 'General',
              type: 'subfolder',
              children: {}
            };
          }
          organized[move.targetFolder].children[subfolderKey].children[move.id] = {
            ...move,
            contentType: 'move'
          };
        }
        return;
      }
      // 2) Fallback: auto-place par type
      const folderKey = getFolderKeyForType(move.type);
      if (organized[folderKey]) {
        const subfolderKey = 'Basic ' + move.type.charAt(0).toUpperCase() + move.type.slice(1);
        if (!organized[folderKey].children[subfolderKey]) {
          organized[folderKey].children[subfolderKey] = {
            name: subfolderKey,
            type: 'subfolder',
            children: {}
          };
        }
        organized[folderKey].children[subfolderKey].children[move.id] = {
          ...move,
          contentType: 'move'
        };
      }
    });

    // Organiser les combos (toujours utiliser les données fraîches de l'API)
    combos.forEach(combo => {
      // Supprimer l'ancien combo s'il existe déjà dans la structure
      Object.keys(organized).forEach(folderKey => {
        Object.keys(organized[folderKey].children).forEach(subfolderKey => {
          if (organized[folderKey].children[subfolderKey].children[combo.id]) {
            delete organized[folderKey].children[subfolderKey].children[combo.id];
          }
        });
      });
      
      // Maintenant ajouter le combo avec ses vraies données
        // Si le combo a un dossier cible spécifié, l'organiser dans ce dossier
        if (combo.targetFolder && combo.targetFolder !== "null" && combo.targetFolder !== null) {
          // Créer le dossier s'il n'existe pas
          if (!organized[combo.targetFolder]) {
            organized[combo.targetFolder] = {
              name: combo.targetFolder,
              type: 'folder',
              children: {}
            };
          }
          
          if (combo.targetSubfolder) {
            // Si un subfolder est spécifié, l'utiliser
            if (!organized[combo.targetFolder].children[combo.targetSubfolder]) {
              organized[combo.targetFolder].children[combo.targetSubfolder] = {
                name: combo.targetSubfolder,
                type: 'subfolder',
                children: {}
              };
            }
            organized[combo.targetFolder].children[combo.targetSubfolder].children[combo.id] = {
              ...combo,
              contentType: 'combo'
            };
          } else {
            // Si pas de subfolder, créer un dossier "General" ou utiliser le premier subfolder existant
            let subfolderKey = 'General';
            if (Object.keys(organized[combo.targetFolder].children).length > 0) {
              subfolderKey = Object.keys(organized[combo.targetFolder].children)[0];
            } else {
              // Créer un dossier "General" s'il n'y en a pas
              organized[combo.targetFolder].children[subfolderKey] = {
                name: 'General',
                type: 'subfolder',
                children: {}
              };
            }
            organized[combo.targetFolder].children[subfolderKey].children[combo.id] = {
              ...combo,
              contentType: 'combo'
            };
          }
        } else if (organized['Combos']) {
          // Sinon, utiliser la logique par défaut
          let subfolderKey = 'Technical Combos';
          if (combo.name.toLowerCase().includes('ending') || combo.description?.toLowerCase().includes('ending')) {
            subfolderKey = 'Ending Combos';
          } else if (combo.name.toLowerCase().includes('battle') || combo.description?.toLowerCase().includes('battle')) {
            subfolderKey = 'Battle Combos';
          }
          
          if (!organized['Combos'].children[subfolderKey]) {
            organized['Combos'].children[subfolderKey] = {
              name: subfolderKey,
              type: 'subfolder',
              children: {}
            };
          }
          organized['Combos'].children[subfolderKey].children[combo.id] = {
            ...combo,
            contentType: 'combo'
          };
        }
    });

    return organized;
  };

  const getFolderKeyForType = (type) => {
    const mapping = {
      'toprock': 'TopRock',
      'footwork': 'Footwork',
      'powermoves': 'PowerMoves',
      'freezes': 'Freezes',
      'transitions': 'Transitions',
      'flips': 'Flips'
    };
    return mapping[type] || 'TopRock';
  };

  const getTypeColor = (type) => {
    const colors = {
      toprock: 'bg-blue-100 text-blue-800 border-blue-200',
      footwork: 'bg-green-100 text-green-800 border-green-200',
      powermoves: 'bg-red-100 text-red-800 border-red-200',
      freezes: 'bg-purple-100 text-purple-800 border-purple-200',
      transitions: 'bg-orange-100 text-orange-800 border-orange-200',
      flips: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return colors[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const toggleFolder = (folderKey) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderKey)) {
        newSet.delete(folderKey);
      } else {
        newSet.add(folderKey);
      }
      return newSet;
    });
  };

  const toggleSubfolder = (folderKey, subfolderKey) => {
    const key = `${folderKey}-${subfolderKey}`;
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handleAddFolder = () => {
    setEditingFolder(null);
    setShowFolderModal(true);
  };

  const handleAddSubfolder = (parentFolderKey) => {
    setParentFolder(parentFolderKey);
    setEditingSubfolder(null);
    setShowSubfolderModal(true);
  };

  const handleEditFolder = (folderKey) => {
    if (!folders || !folders[folderKey]) return;
    setEditingFolder({ key: folderKey, name: folders[folderKey].name });
    setShowFolderModal(true);
  };

  const handleEditSubfolder = (folderKey, subfolderKey) => {
    if (!folders || !folders[folderKey] || !folders[folderKey].children[subfolderKey]) return;
    setParentFolder(folderKey);
    setEditingSubfolder({ key: subfolderKey, name: folders[folderKey].children[subfolderKey].name });
    setShowSubfolderModal(true);
  };

  const handleSaveFolder = (name) => {
    if (!folders) return;
    if (editingFolder) {
      // Éditer un dossier existant
      updateFolders({
        ...folders,
        [editingFolder.key]: {
          ...folders[editingFolder.key],
          name: name
        }
      });
    } else {
      // Créer un nouveau dossier
      const newKey = name.replace(/\s+/g, '');
      updateFolders({
        ...folders,
        [newKey]: {
          name: name,
          type: 'folder',
          children: {}
        }
      });
      setExpandedFolders(prev => new Set([...prev, newKey]));
    }
    setShowFolderModal(false);
  };

  const handleSaveSubfolder = (name) => {
    if (!folders || !parentFolder) return;
    if (editingSubfolder) {
      // Éditer un sous-dossier existant
      updateFolders({
        ...folders,
        [parentFolder]: {
          ...folders[parentFolder],
          children: {
            ...folders[parentFolder].children,
            [editingSubfolder.key]: {
              ...folders[parentFolder].children[editingSubfolder.key],
              name: name
            }
          }
        }
      });
    } else {
      // Créer un nouveau sous-dossier
      const newKey = name.replace(/\s+/g, '');
      updateFolders({
        ...folders,
        [parentFolder]: {
          ...folders[parentFolder],
          children: {
            ...folders[parentFolder].children,
            [newKey]: {
              name: name,
              type: 'subfolder',
              children: {}
            }
          }
        }
      });
      setExpandedFolders(prev => new Set([...prev, `${parentFolder}-${newKey}`]));
    }
    setShowSubfolderModal(false);
  };

  const handleDeleteFolder = (folderKey) => {
    if (!folders) return;
    const newFolders = { ...folders };
    delete newFolders[folderKey];
    updateFolders(newFolders);
  };

  const handleDeleteSubfolder = (folderKey, subfolderKey) => {
    if (!folders) return;
    updateFolders({
      ...folders,
      [folderKey]: {
        ...folders[folderKey],
        children: {
          ...folders[folderKey].children,
          [subfolderKey]: undefined
        }
      }
    });
  };

  const handleMoveItemToFolder = (item, targetFolderKey, targetSubfolderKey) => {
    if (!folders) return;
    console.log('Moving item:', item, 'to folder:', targetFolderKey, 'subfolder:', targetSubfolderKey);
    
    const newFolders = JSON.parse(JSON.stringify(folders)); // Deep copy
    
    // Retirer l'item de son emplacement actuel
    Object.keys(newFolders).forEach(folderKey => {
      Object.keys(newFolders[folderKey].children).forEach(subfolderKey => {
        if (newFolders[folderKey].children[subfolderKey].children[item.id]) {
          delete newFolders[folderKey].children[subfolderKey].children[item.id];
        }
      });
    });
    
    // Ajouter l'item au nouvel emplacement
    if (newFolders[targetFolderKey] && newFolders[targetFolderKey].children[targetSubfolderKey]) {
      newFolders[targetFolderKey].children[targetSubfolderKey].children[item.id] = {
        ...item,
        contentType: item.contentType || 'move'
      };
      console.log('Item moved successfully');
    } else {
      console.log('Target folder or subfolder not found');
    }
    
    updateFolders(newFolders);
  };

  const handleOpenMoveToFolderModal = (item) => {
    setSelectedItemToMove(item);
    setShowMoveToFolderModal(true);
  };

  const expandAll = () => {
    if (!folders) return;
    const allKeys = new Set();
    Object.keys(folders).forEach(folderKey => {
      allKeys.add(folderKey);
      Object.keys(folders[folderKey].children).forEach(subfolderKey => {
        allKeys.add(`${folderKey}-${subfolderKey}`);
      });
    });
    setExpandedFolders(allKeys);
  };

  const collapseAll = () => {
    setExpandedFolders(new Set());
  };

  const organizedContent = folders ? organizeContent() : {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Tree View Encyclopedia
          </h2>
          <p className="text-muted-foreground mt-1">
            Organized view of {moves.length} breaking moves and {combos.length} combos
          </p>
        </div>
        
        {/* Action Buttons - Mobile Responsive */}
        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={onAddMove} 
            size="sm"
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200"
          >
            <Plus className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Add Move</span>
            <span className="sm:hidden">Move</span>
          </Button>
          <Button 
            onClick={onAddCombo} 
            size="sm"
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200"
          >
            <Zap className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Add Combo</span>
            <span className="sm:hidden">Combo</span>
          </Button>
          <Button 
            onClick={handleAddFolder} 
            variant="outline"
            size="sm"
            className="border-2 border-dashed border-gray-300 hover:border-gray-400"
          >
            <Folder className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Add Folder</span>
            <span className="sm:hidden">Folder</span>
          </Button>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={expandAll}
              className="text-xs"
            >
              <ChevronsDown className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Expand All</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={collapseAll}
              className="text-xs"
            >
              <ChevronsUp className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Collapse All</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search moves and combos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 focus:ring-2 focus:ring-blue-500 transition-all duration-200"
        />
      </div>

      {/* Tree View */}
      <div className="space-y-2">
        {organizedContent && Object.entries(organizedContent).map(([folderKey, folder]) => (
          <div key={folderKey} className="border rounded-lg">
            {/* Folder Header */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <button
                  onClick={() => toggleFolder(folderKey)}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded flex-shrink-0"
                >
                  {expandedFolders.has(folderKey) ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                {expandedFolders.has(folderKey) ? (
                  <FolderOpen className="w-4 h-4 text-blue-600 flex-shrink-0" />
                ) : (
                  <Folder className="w-4 h-4 text-blue-600 flex-shrink-0" />
                )}
                <span className="font-medium truncate">{folder.name}</span>
                <Badge className="bg-blue-100 text-blue-800 border-blue-200 flex-shrink-0">
                  <span className="hidden sm:inline">{Object.keys(folder.children).length} subfolders</span>
                  <span className="sm:hidden">{Object.keys(folder.children).length}</span>
                </Badge>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => handleAddSubfolder(folderKey)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Subfolder
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleEditFolder(folderKey)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Folder
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => moveFolderUp(folderKey)}>
                    <ChevronUp className="w-4 h-4 mr-2" />
                    Move Up
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => moveFolderDown(folderKey)}>
                    <ChevronDownIcon className="w-4 h-4 mr-2" />
                    Move Down
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => handleDeleteFolder(folderKey)}
                    className="text-red-600"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Folder
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Subfolders */}
            {expandedFolders.has(folderKey) && (
              <div className="pl-6 space-y-2">
                {Object.entries(folder.children).map(([subfolderKey, subfolder]) => (
                  <div key={subfolderKey} className="border-l-2 border-gray-200 dark:border-gray-700 pl-4">
                    {/* Subfolder Header */}
                    <div className="flex items-center justify-between p-2 bg-gray-25 dark:bg-gray-750">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <button
                          onClick={() => toggleSubfolder(folderKey, subfolderKey)}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded flex-shrink-0"
                        >
                          {expandedFolders.has(`${folderKey}-${subfolderKey}`) ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                        <span className="font-medium text-sm truncate">{subfolder.name}</span>
                        <Badge className="bg-green-100 text-green-800 border-green-200 text-xs flex-shrink-0">
                          <span className="hidden sm:inline">{Object.keys(subfolder.children).length} items</span>
                          <span className="sm:hidden">{Object.keys(subfolder.children).length}</span>
                        </Badge>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-3 h-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => handleEditSubfolder(folderKey, subfolderKey)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Subfolder
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => moveSubfolderUp(folderKey, subfolderKey)}>
                            <ChevronUp className="w-4 h-4 mr-2" />
                            Move Up
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => moveSubfolderDown(folderKey, subfolderKey)}>
                            <ChevronDownIcon className="w-4 h-4 mr-2" />
                            Move Down
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDeleteSubfolder(folderKey, subfolderKey)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Subfolder
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {/* Items in subfolder */}
                    {expandedFolders.has(`${folderKey}-${subfolderKey}`) && (
                      <div className="pl-4 space-y-2">
                        {Object.entries(subfolder.children)
                          .filter(([_, item]) => 
                            searchTerm === '' || 
                            item.name.toLowerCase().includes(searchTerm.toLowerCase())
                          )
                          .map(([itemId, item]) => (
                            <Card 
                              key={itemId} 
                              className={`cursor-pointer transition-all duration-200 ${theme === 'light' ? 'bg-white border-gray-200 text-black hover:bg-gray-50' : 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'}`}
                              onClick={() => setSelectedMove(item)}
                            >
                              <CardContent className="p-3">
                                <div className="space-y-2">
                                  {/* Item Header */}
                                  <div className="flex items-center gap-2">
                                    {item.contentType === 'combo' ? (
                                      <Zap className="w-4 h-4 text-purple-600" />
                                    ) : (
                                      <ExternalLink className="w-4 h-4 text-blue-600" />
                                    )}
                                    <span className="font-medium">{item.name}</span>
                                    {item.contentType === 'combo' && item.description && (
                                      <p className="text-xs text-gray-600 max-w-xs truncate" title={item.description}>
                                        {item.description}
                                      </p>
                                    )}
                                    <div className="flex flex-col gap-1">
                                      {/* Étoile signature au-dessus de l'étiquette */}
                                      <button
                                        className="flex items-center justify-center w-4 h-4"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (item.contentType === 'combo') {
                                            setComboSignature(item.id, !item.isSignature);
                                          } else {
                                            setMoveSignature(item.id, !item.isSignature);
                                          }
                                        }}
                                        title={item.isSignature ? 'Unmark Signature' : 'Mark Signature'}
                                      >
                                        <span className={`${item.isSignature ? 'text-yellow-500' : 'text-gray-400'} text-sm`}>★</span>
                                        <span className="sr-only">{item.isSignature ? 'Unmark Signature' : 'Mark Signature'}</span>
                                      </button>
                                      
                                      {item.contentType === 'combo' ? (
                                        <div className="flex gap-1">
                                          <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-xs">Combo</Badge>
                                          <Badge className="bg-gray-100 text-gray-800 border-gray-200 text-xs">{item.moves?.length || 0} items</Badge>
                                        </div>
                                      ) : (
                                        <Badge className={`text-xs ${getTypeColor(item.type)}`}>
                                          {item.type}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Action Buttons - Mobile Responsive */}
                                  <div className="flex flex-wrap gap-1">
                                    {item.contentType === 'move' && (
                                      <>
                                        <Button
                                          size="sm"
                                          variant={arsenal.includes(item.id) ? "default" : "outline"}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            arsenal.includes(item.id) 
                                              ? removeFromArsenal(item.id)
                                              : addToArsenal(item.id);
                                          }}
                                          className="text-xs px-1 sm:px-2 py-1"
                                        >
                                          <span className="hidden sm:inline">{arsenal.includes(item.id) ? 'Arsenal' : 'Add to Arsenal'}</span>
                                          <span className="sm:hidden">{arsenal.includes(item.id) ? 'Arsenal' : 'Add'}</span>
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant={learning.includes(item.id) ? "default" : "outline"}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            learning.includes(item.id)
                                              ? removeFromLearning(item.id)
                                              : addToLearning(item.id);
                                          }}
                                          className="text-xs px-1 sm:px-2 py-1"
                                        >
                                          <span className="hidden sm:inline">{learning.includes(item.id) ? 'Learning' : 'Add to Learning'}</span>
                                          <span className="sm:hidden">{learning.includes(item.id) ? 'Learning' : 'Learn'}</span>
                                        </Button>
                                      </>
                                    )}
                                                                         {item.contentType === 'combo' && (
                                       <>
                                         {item.videoLink && (
                                           <Button
                                             size="sm"
                                             variant="outline"
                                             onClick={(e) => { e.stopPropagation(); window.open(item.videoLink, '_blank'); }}
                                             className="text-xs px-1 sm:px-2 py-1 text-blue-600 hover:text-blue-700"
                                           >
                                             <ExternalLink className="w-3 h-3 mr-1" />
                                             <span className="hidden sm:inline">Video</span>
                                           </Button>
                                         )}
                                         <Button
                                           size="sm"
                                           variant="outline"
                                           onClick={(e) => { e.stopPropagation(); onEditCombo(item); }}
                                           className="text-xs px-1 sm:px-2 py-1"
                                         >
                                           Edit
                                         </Button>
                                       </>
                                     )}
                                     {item.contentType === 'move' && (
                                       <Button
                                         size="sm"
                                         variant="outline"
                                         onClick={(e) => { e.stopPropagation(); onEditMove(item); }}
                                         className="text-xs px-1 sm:px-2 py-1"
                                       >
                                         Edit
                                       </Button>
                                     )}
                                     <Button
                                       size="sm"
                                       variant="outline"
                                       onClick={(e) => { e.stopPropagation(); handleOpenMoveToFolderModal(item); }}
                                       className="text-xs px-1 sm:px-2 py-1"
                                     >
                                       <span className="hidden sm:inline">Move to Folder</span>
                                       <span className="sm:hidden">Move</span>
                                     </Button>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Folder Modal */}
      <Dialog open={showFolderModal} onOpenChange={setShowFolderModal}>
        <DialogContent className={`sm:max-w-md ${theme === 'light' ? 'bg-white border-black text-black' : 'bg-black border-white text-white'}`}>
          <DialogHeader>
            <DialogTitle>
              {editingFolder ? 'Edit Folder' : 'Add New Folder'}
            </DialogTitle>
          </DialogHeader>
          <FolderForm 
            initialName={editingFolder?.name || ''}
            onSave={handleSaveFolder}
            onCancel={() => setShowFolderModal(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Subfolder Modal */}
      <Dialog open={showSubfolderModal} onOpenChange={setShowSubfolderModal}>
        <DialogContent className={`sm:max-w-md ${theme === 'light' ? 'bg-white border-black text-black' : 'bg-black border-white text-white'}`}>
          <DialogHeader>
            <DialogTitle>
              {editingSubfolder ? 'Edit Subfolder' : 'Add New Subfolder'}
            </DialogTitle>
          </DialogHeader>
          <FolderForm 
            initialName={editingSubfolder?.name || ''}
            onSave={handleSaveSubfolder}
            onCancel={() => setShowSubfolderModal(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Move/Combo Detail Modal */}
      {selectedMove && !showStretch && (
        <Dialog open={!!selectedMove && !showStretch} onOpenChange={() => setSelectedMove(null)}>
          <DialogContent className={`sm:max-w-2xl ${theme === 'light' ? 'bg-white border-black text-black' : 'bg-black border-white text-white'}`}>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                {selectedMove.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {selectedMove.description && (
                <p className="text-muted-foreground">{selectedMove.description}</p>
              )}
              
              {selectedMove.videoUrl && (
                <div className="flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  <a 
                    href={selectedMove.videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Watch Tutorial
                  </a>
                </div>
              )}

              {selectedMove.contentType === 'combo' && selectedMove.moves && (
                <div className="space-y-2">
                  <span className={`font-medium text-sm ${theme === 'light' ? 'text-gray-800' : 'text-gray-300'}`}>Items in this combo:</span>
                  <div className="space-y-2">
                    {selectedMove.moves.map((move, index) => (
                      <div key={move.uniqueId || move.id} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                        <span className="text-sm font-medium">{index + 1}.</span>
                        <span className="text-sm">{move.name}</span>
                        {move.itemType === 'combo' ? (
                          <div className="flex gap-1">
                            <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-xs">Combo</Badge>
                            <Badge className="bg-gray-100 text-gray-800 border-gray-200 text-xs">{move.moves?.length || 0} items</Badge>
                          </div>
                        ) : (
                          <Badge className={`text-xs ${getTypeColor(move.type)}`}>
                            {move.type}
                          </Badge>
                        )}
                        {move.variations && move.variations.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {move.variations.map((variation, vIndex) => (
                              <Badge key={vIndex} className="bg-green-100 text-green-800 border-green-200 text-xs">
                                {variation}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Boutons pour lancer des sessions (seulement pour les moves) */}
              {selectedMove?.contentType !== 'combo' && (
                <div className="space-y-2 mt-4">
                  <Button 
                    onClick={() => setShowConditioning(true)}
                    className="w-full bg-blue-600 text-white font-bold"
                  >
                    Start Conditioning Session
                  </Button>
                  <Button 
                    onClick={() => setShowMobility(true)}
                    className="w-full bg-purple-600 text-white font-bold"
                  >
                    Start Mobility Session
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Conditioning Session */}
      {showConditioning && selectedMove && (
        <Dialog open={showConditioning} onOpenChange={(open) => {
          setShowConditioning(open);
          if (!open) {
            setSelectedMove(null);
          }
        }}>
          <DialogContent className={`sm:max-w-2xl ${theme === 'light' ? 'bg-white border-black text-black' : 'bg-black border-white text-white'}`}>
            <ConditioningSession move={selectedMove.name} level="beginner" onClose={() => {
              setShowConditioning(false);
              setSelectedMove(null);
            }} />
          </DialogContent>
        </Dialog>
      )}

      {/* Mobility Session */}
      {showMobility && selectedMove && (
        <Dialog open={showMobility} onOpenChange={(open) => {
          setShowMobility(open);
          if (!open) {
            setSelectedMove(null);
          }
        }}>
          <DialogContent className={`sm:max-w-2xl ${theme === 'light' ? 'bg-white border-black text-black' : 'bg-black border-white text-white'}`}>
            <MobilitySession move={selectedMove.name} level="beginner" onClose={() => {
              setShowMobility(false);
              setSelectedMove(null);
            }} />
          </DialogContent>
        </Dialog>
      )}

      {/* Move to Folder Modal */}
      <Dialog open={showMoveToFolderModal} onOpenChange={setShowMoveToFolderModal}>
        <DialogContent className={`sm:max-w-md ${theme === 'light' ? 'bg-white border-black text-black' : 'bg-black border-white text-white'}`}>
          <DialogHeader>
            <DialogTitle>Move "{selectedItemToMove?.name}" to Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select a folder and subfolder to move this item to:
            </p>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {folders && Object.entries(folders).map(([folderKey, folder]) => (
                <div key={folderKey} className="border rounded p-3">
                  <h4 className="font-medium mb-2">{folder.name}</h4>
                  <div className="space-y-1">
                    {Object.entries(folder.children).map(([subfolderKey, subfolder]) => (
                      <Button
                        key={subfolderKey}
                        variant="outline"
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => {
                          console.log('Button clicked with item:', selectedItemToMove);
                          handleMoveItemToFolder(selectedItemToMove, folderKey, subfolderKey);
                          setShowMoveToFolderModal(false);
                        }}
                      >
                        📁 {subfolder.name}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Composant pour le formulaire de dossier
const FolderForm = ({ initialName, onSave, onCancel }) => {
  const [name, setName] = useState(initialName);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim()) {
      onSave(name.trim());
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="folderName">Folder Name</Label>
        <Input
          id="folderName"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter folder name..."
          autoFocus
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!name.trim()}>
          Save
        </Button>
      </div>
    </form>
  );
};

export default TreeViewEncyclopedia; 